// src/pages/finance-portal/procurement-pos/ProcurementPODetail.js
//
// Procurement PO Detail with full workflow + goods receipt.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, IconButton, Card, Chip, Alert, Skeleton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import EmailIcon from '@mui/icons-material/Email';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LockIcon from '@mui/icons-material/Lock';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const STATUS_COLORS = {
    DRAFT:               { bg: '#f1f5f9', color: '#64748b' },
    PENDING_APPROVAL:    { bg: '#fef9c3', color: '#854d0e' },
    APPROVED:            { bg: '#dcfce7', color: '#166534' },
    SENT:                { bg: '#dbeafe', color: '#1d4ed8' },
    ACKNOWLEDGED:        { bg: '#e0e7ff', color: '#4338ca' },
    PARTIALLY_RECEIVED:  { bg: '#fef3c7', color: '#a16207' },
    FULLY_RECEIVED:      { bg: '#dcfce7', color: '#166534' },
    CLOSED:              { bg: '#f1f5f9', color: '#475569' },
    CANCELLED:           { bg: '#fecaca', color: '#991b1b' },
};

function fmtMoney(val, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
        style: 'currency', currency, maximumFractionDigits: 2,
    }).format(val || 0);
}

export default function ProcurementPODetail() {
    const { poId } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [po, setPo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [cancelDialog, setCancelDialog] = useState({ open: false, reason: '' });
    const [receiveDialog, setReceiveDialog] = useState({ open: false, qtys: {} });

    const fetch = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`/api/finance/procurement-pos/${poId}`);
            if (res.data.success) setPo(res.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [poId]);

    useEffect(() => { fetch(); }, [fetch]);

    const transition = async (action, body) => {
        try {
            const res = await axiosInstance.post(
                `/api/finance/procurement-pos/${poId}/${action}`, body || {});
            enqueueSnackbar(res.data?.message || 'Done', { variant: 'success' });
            fetch();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const submitCancel = async () => {
        await transition('cancel', { reason: cancelDialog.reason || null });
        setCancelDialog({ open: false, reason: '' });
    };

    const openReceive = () => {
        const qtys = {};
        (po?.lines || []).forEach(l => {
            qtys[l.id] = '';
        });
        setReceiveDialog({ open: true, qtys });
    };

    const submitReceive = async () => {
        const receipts = Object.entries(receiveDialog.qtys)
            .filter(([_, v]) => v && parseFloat(v) > 0)
            .map(([line_id, qty]) => ({
                line_id: parseInt(line_id, 10),
                qty_received_now: parseFloat(qty),
            }));
        if (receipts.length === 0) {
            enqueueSnackbar('Enter at least one quantity', { variant: 'warning' });
            return;
        }
        try {
            const res = await axiosInstance.post(
                `/api/finance/procurement-pos/${poId}/receive`,
                { receipts },
            );
            enqueueSnackbar(res.data?.message || 'Receipts recorded', { variant: 'success' });
            setReceiveDialog({ open: false, qtys: {} });
            fetch();
        } catch (err) {
            const errs = err.response?.data?.errors;
            if (errs) {
                const lines = Object.entries(errs).map(([k, v]) => `${k}: ${v}`).join('\n');
                enqueueSnackbar(`Validation failed:\n${lines}`, { variant: 'error' });
            } else {
                enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
            }
        }
    };

    const labelCell = { fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b',
                        textTransform: 'uppercase', bgcolor: '#f8fafc',
                        borderBottom: '2px solid #e2e8f0', letterSpacing: '0.05em' };
    const dataCell = { fontFamily, fontSize: '0.85rem', color: '#475569' };

    if (loading) return <Box sx={{ p: 4, fontFamily }}><Skeleton height={400} /></Box>;
    if (!po) return (
        <Box sx={{ p: 4, fontFamily }}>
            <Alert severity="error">{error || 'PO not found'}</Alert>
            <Button onClick={() => navigate('/finance-portal/procurement-pos')} sx={{ mt: 2, fontFamily }}>
                Back to list
            </Button>
        </Box>
    );

    const sStyle = STATUS_COLORS[po.status] || STATUS_COLORS.DRAFT;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton onClick={() => navigate('/finance-portal/procurement-pos')}
                                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
                            Procurement Purchase Order
                        </Typography>
                        <Typography variant="h5" sx={{ fontFamily, fontWeight: 800, color: 'white' }}>
                            {po.po_number}
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem', mt: 0.25 }}>
                            {po.vendor_name}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ mt: 1 }}>
                    <Chip label={po.status.replace(/_/g, ' ')} size="small"
                          sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem',
                                bgcolor: sStyle.bg, color: sStyle.color }} />
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1200, mx: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {po.cancellation_reason && po.status === 'CANCELLED' && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>
                        <strong>Cancelled:</strong> {po.cancellation_reason}
                    </Alert>
                )}

                {po.requisition_id && (
                    <Alert severity="info" action={
                        <Button size="small" onClick={() => navigate(`/finance-portal/requisitions/${po.requisition_id}`)}
                                sx={{ fontFamily, textTransform: 'none' }}>
                            Open Requisition
                        </Button>
                    } sx={{ mb: 2, borderRadius: '12px', fontFamily }}>
                        Created from requisition {po.requisition_number || `#${po.requisition_id}`}
                    </Alert>
                )}

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                    <Box sx={{ display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                              gap: 2 }}>
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8',
                                              textTransform: 'uppercase' }}>Order Date</Typography>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.95rem' }}>
                                {po.order_date}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8',
                                              textTransform: 'uppercase' }}>Expected Delivery</Typography>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.95rem' }}>
                                {po.expected_delivery_date || '—'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8',
                                              textTransform: 'uppercase' }}>Payment Terms</Typography>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.95rem' }}>
                                {po.payment_terms?.replace('_', ' ')}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8',
                                              textTransform: 'uppercase' }}>Total</Typography>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>
                                {fmtMoney(po.total_amount, po.currency)}
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {po.status === 'DRAFT' && (
                            <>
                                <Button startIcon={<EditIcon />} variant="outlined" size="small"
                                        onClick={() => navigate(`/finance-portal/procurement-pos/${po.id}/edit`)}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
                                    Edit
                                </Button>
                                <Button startIcon={<SendIcon />} variant="contained" size="small"
                                        onClick={() => transition('submit')}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                              borderRadius: '10px', bgcolor: '#22c55e' }}>
                                    Submit for Approval
                                </Button>
                            </>
                        )}
                        {po.status === 'PENDING_APPROVAL' && (
                            <Button startIcon={<CheckIcon />} variant="contained" size="small"
                                    onClick={() => transition('approve')}
                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                          borderRadius: '10px', bgcolor: '#16a34a' }}>
                                Approve
                            </Button>
                        )}
                        {po.status === 'APPROVED' && (
                            <Button startIcon={<EmailIcon />} variant="contained" size="small"
                                    onClick={() => transition('send')}
                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                          borderRadius: '10px', bgcolor: '#3b82f6' }}>
                                Mark as Sent
                            </Button>
                        )}
                        {po.status === 'SENT' && (
                            <Button startIcon={<HandshakeIcon />} variant="contained" size="small"
                                    onClick={() => transition('acknowledge')}
                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                          borderRadius: '10px', bgcolor: '#6366f1' }}>
                                Mark Acknowledged
                            </Button>
                        )}
                        {(po.status === 'ACKNOWLEDGED' ||
                          po.status === 'PARTIALLY_RECEIVED' ||
                          po.status === 'SENT') && (
                            <Button startIcon={<ReceiptLongIcon />} variant="contained" size="small"
                                    onClick={openReceive}
                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                          borderRadius: '10px', bgcolor: '#f59e0b' }}>
                                Record Receipt
                            </Button>
                        )}
                        {(po.status === 'PARTIALLY_RECEIVED' || po.status === 'FULLY_RECEIVED') && (
                            <Button startIcon={<LockIcon />} variant="outlined" size="small"
                                    onClick={() => transition('close')}
                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
                                Close PO
                            </Button>
                        )}
                        {!['CLOSED', 'CANCELLED'].includes(po.status) && (
                            <Button startIcon={<CancelIcon />} variant="text" size="small"
                                    onClick={() => setCancelDialog({ open: true, reason: '' })}
                                    sx={{ fontFamily, textTransform: 'none', color: '#94a3b8' }}>
                                Cancel
                            </Button>
                        )}
                    </Box>
                </Card>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem' }}>
                            Lines
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={labelCell}>Description</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Qty Ordered</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Qty Received</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Unit Price</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Line Total</TableCell>
                                    <TableCell sx={labelCell}>GL Account</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(po.lines || []).map(l => (
                                    <TableRow key={l.id}>
                                        <TableCell sx={{ ...dataCell, color: '#1e293b', fontWeight: 500 }}>
                                            {l.description}
                                        </TableCell>
                                        <TableCell sx={{ ...dataCell, textAlign: 'right' }}>{l.qty_ordered}</TableCell>
                                        <TableCell sx={{ ...dataCell, textAlign: 'right',
                                                          color: l.qty_received >= l.qty_ordered ? '#16a34a' : '#475569',
                                                          fontWeight: 600 }}>
                                            {l.qty_received}
                                        </TableCell>
                                        <TableCell sx={{ ...dataCell, textAlign: 'right' }}>
                                            {fmtMoney(l.unit_price, po.currency)}
                                        </TableCell>
                                        <TableCell sx={{ ...dataCell, textAlign: 'right', fontWeight: 600, color: '#1e293b' }}>
                                            {fmtMoney(l.line_total, po.currency)}
                                        </TableCell>
                                        <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                            {l.gl_account_number ? `${l.gl_account_number} — ${l.gl_account_name}` : '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Divider />
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', gap: 4 }}>
                            <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                                Subtotal: <strong style={{ color: '#1e293b' }}>{fmtMoney(po.subtotal, po.currency)}</strong>
                            </Typography>
                            <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                                Tax: <strong style={{ color: '#1e293b' }}>{fmtMoney(po.tax_amount, po.currency)}</strong>
                            </Typography>
                            <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                                Shipping: <strong style={{ color: '#1e293b' }}>{fmtMoney(po.shipping_amount, po.currency)}</strong>
                            </Typography>
                        </Box>
                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.2rem', color: '#0f172a' }}>
                            Total: {fmtMoney(po.total_amount, po.currency)}
                        </Typography>
                    </Box>
                </Card>
            </Box>

            <Dialog open={cancelDialog.open}
                    onClose={() => setCancelDialog({ open: false, reason: '' })}
                    PaperProps={{ sx: { borderRadius: '16px' } }} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Cancel PO</DialogTitle>
                <DialogContent>
                    <TextField fullWidth multiline rows={2} label="Reason (optional)"
                               value={cancelDialog.reason}
                               onChange={e => setCancelDialog(prev => ({ ...prev, reason: e.target.value }))}
                               InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                               InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCancelDialog({ open: false, reason: '' })}
                            sx={{ fontFamily, textTransform: 'none' }}>Back</Button>
                    <Button variant="contained" onClick={submitCancel}
                            sx={{ fontFamily, textTransform: 'none', bgcolor: '#ef4444',
                                  '&:hover': { bgcolor: '#dc2626' } }}>
                        Cancel PO
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={receiveDialog.open}
                    onClose={() => setReceiveDialog({ open: false, qtys: {} })}
                    PaperProps={{ sx: { borderRadius: '16px' } }} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Record Goods Receipt</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2, borderRadius: '10px', fontFamily, fontSize: '0.8rem' }}>
                        Enter the quantity received now for each line. Leave blank for lines you're not receiving.
                    </Alert>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={labelCell}>Description</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Ordered</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Already Received</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Outstanding</TableCell>
                                    <TableCell sx={{ ...labelCell, width: 150 }}>Receive Now</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(po.lines || []).map(l => {
                                    const outstanding = (l.qty_ordered || 0) - (l.qty_received || 0);
                                    return (
                                        <TableRow key={l.id}>
                                            <TableCell sx={dataCell}>{l.description}</TableCell>
                                            <TableCell sx={{ ...dataCell, textAlign: 'right' }}>{l.qty_ordered}</TableCell>
                                            <TableCell sx={{ ...dataCell, textAlign: 'right' }}>{l.qty_received}</TableCell>
                                            <TableCell sx={{ ...dataCell, textAlign: 'right',
                                                              fontWeight: 600,
                                                              color: outstanding > 0 ? '#1e293b' : '#94a3b8' }}>
                                                {outstanding}
                                            </TableCell>
                                            <TableCell>
                                                <TextField size="small" type="number"
                                                           disabled={outstanding <= 0}
                                                           value={receiveDialog.qtys[l.id] || ''}
                                                           onChange={e => setReceiveDialog(prev => ({
                                                               ...prev,
                                                               qtys: { ...prev.qtys, [l.id]: e.target.value },
                                                           }))}
                                                           inputProps={{ max: outstanding, min: 0 }}
                                                           InputProps={{ sx: { fontFamily, fontSize: '0.82rem' } }} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setReceiveDialog({ open: false, qtys: {} })}
                            sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={submitReceive}
                            sx={{ fontFamily, textTransform: 'none', bgcolor: '#f59e0b',
                                  '&:hover': { bgcolor: '#d97706' } }}>
                        Record Receipt
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
